import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { doesPolylineIntersectPolygon, calculateBufferPolygonsAreaM2 } from '../utils/geometryUtils';
import { elevationService } from '../services/elevationService';
import { useEventSubscribe } from '../events/hooks';
import { useCustomDataSource } from './common/useCustomDataSource';
import { CONNECTION_THRESHOLD_METERS, DEFAULT_HIGHLIGHT_COLOR, DEFAULT_HIGHLIGHT_LINE_WIDTH, computeCoordHash, getHighlightWidth, extractLineCoordinates, haversineDistance } from './streamHighlight/streamUtils';
import { DEFAULT_BUFFER_UNIT, extractStreamThreshold, isStreamArtifact } from '../utils/hydrologyLegend';
import { findAdjacentStreams as findAdjacentStreamsFn } from './streamHighlight/streamSelection';
import { createMergedBufferEntities } from './streamHighlight/streamBufferMerge';
export { computeCoordHash };
export const useStreamHighlight = ({ viewer, loadedArtifacts, isArtifactVisible, constraintPolygon, projectId, isActive = true }) => {
    const [highlightedStreams, setHighlightedStreams] = useState(new Map());
    const [pendingClick, setPendingClick] = useState(null);
    const [selectedStreams, setSelectedStreams] = useState([]);
    const [selectableEntityIds, setSelectableEntityIds] = useState(new Set());
    const [isCalculatingBuffer, setIsCalculatingBuffer] = useState(false);
    const [pendingRestores, setPendingRestores] = useState([]);
    const [hoveredStream, setHoveredStream] = useState(null);
    // Snapshot of the highlight as it existed when the user clicked it
    // for editing. Used to revert colour, width, and name on cancel —
    // previewColor and previewLineWidth modify the live highlight in place.
    const editBaselineRef = useRef(null);
    // View-mode anchor: set when a saved artifact is loaded for view-only
    // display (no editBaselineRef). Discard treats it like edit-mode for
    // cleanup (group unhighlight + clear pendingClick). promoteToEdit
    // converts the anchor into an editBaselineRef so the next
    // confirmHighlight takes the edit branch.
    const viewModeAnchorRef = useRef(null);
    const [isViewMode, setIsViewMode] = useState(false);
    // Last hover fraction per highlighted entity. Used by handleMapHover
    // to break ties between near-equally-close polyline segments on
    // S-curves: prefer the candidate whose resulting fraction is closest
    // to the previous fraction, so the chart crosshair doesn't snap to a
    // folded-back segment when the cursor moves quickly.
    const lastHoverFractionByEntityRef = useRef(new Map());
    const [groupVisibility, setGroupVisibility] = useState(new Map());
    const [groupOpacities, setGroupOpacities] = useState(new Map());
    const highlightsDataSourceRef = useCustomDataSource(viewer, 'stream-highlights');
    const buffersDataSourceRef = useCustomDataSource(viewer, 'stream-buffers');
    const selectionDataSourceRef = useCustomDataSource(viewer, 'stream-selection');
    // Synthetic-entity DS for editing server-saved highlights. The saved
    // artifact's geometry has line coordinates but no source layer to
    // pull from, so we synthesize polyline entities here and feed them
    // into the existing in-memory highlight machinery.
    const synthDataSourceRef = useCustomDataSource(viewer, 'stream-synth');
    const selectableDataSourceRef = useCustomDataSource(viewer, 'stream-selectable');
    // Separate DS for the pre-commit "Apply" preview — rendering into a
    // distinct DS keeps the lifecycle independent from the merged-buffer
    // DS (which is driven by committed highlights via updateMergedBuffer).
    const bufferPreviewDataSourceRef = useCustomDataSource(viewer, 'stream-buffer-preview');
    const mergedBufferEntityRef = useRef(null);
    // Map<entityId, bufferPolygon> for the active preview. Populated by
    // previewBuffer, consumed by confirmHighlight to avoid re-fetching.
    const bufferPreviewPolygonsRef = useRef(new Map());
    // Preview's currently-displayed distance/unit. confirmHighlight uses
    // these when bufferConfig isn't explicitly passed.
    const bufferPreviewMetaRef = useRef(null);
    const createPreviewBaselineRef = useRef(new Map());
    // Track the current preview's color/width so subsequent picks can be
    // re-applied to streams added mid-multi-select.
    const createPreviewStyleRef = useRef({
        colorHex: DEFAULT_HIGHLIGHT_COLOR,
        lineWidth: DEFAULT_HIGHLIGHT_LINE_WIDTH,
    });
    // Forward refs so handleMapClick (defined earlier in the file) can
    // invoke the create-preview helpers, which are defined later.
    const applyCreatePreviewRef = useRef(() => { });
    const removeFromCreatePreviewRef = useRef(() => { });
    // Update highlight widths based on camera height
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || highlightedStreams.size === 0)
            return;
        const updateHighlightWidths = () => {
            if (!viewer || viewer.isDestroyed())
                return;
            const cameraHeight = viewer.camera.positionCartographic.height;
            highlightedStreams.forEach((highlight) => {
                var _a;
                const baseWidth = (_a = highlight.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH;
                if (highlight.entity.polyline) {
                    const newWidth = getHighlightWidth(cameraHeight, baseWidth);
                    highlight.entity.polyline.width = new Cesium.ConstantProperty(newWidth);
                }
            });
            if (highlightsDataSourceRef.current) {
                highlightsDataSourceRef.current.entities.values.forEach((entity) => {
                    var _a;
                    const highlightInfo = highlightedStreams.get(entity.id);
                    if (highlightInfo && entity.polyline) {
                        const baseWidth = (_a = highlightInfo.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH;
                        const newWidth = getHighlightWidth(cameraHeight, baseWidth);
                        entity.polyline.width = new Cesium.ConstantProperty(newWidth);
                    }
                });
            }
        };
        const removeListener = viewer.camera.changed.addEventListener(updateHighlightWidths);
        updateHighlightWidths();
        return () => {
            removeListener();
        };
    }, [viewer, highlightedStreams]);
    // Find which artifact a stream belongs to
    const findStreamInfo = useCallback((pickedEntity) => {
        var _a;
        for (const [artifactId, artifact] of loadedArtifacts.entries()) {
            if (artifact.type !== 'vector' || !isStreamArtifact(artifact))
                continue;
            const dataSource = artifact.cesiumObject;
            if ((_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values) {
                for (const entity of dataSource.entities.values) {
                    if (entity === pickedEntity || entity.id === pickedEntity.id) {
                        return { artifact, artifactId };
                    }
                }
            }
        }
        return null;
    }, [loadedArtifacts]);
    // Add visual selection indicator for a stream
    const addSelectionIndicator = useCallback((entityId, entity) => {
        var _a;
        if (!selectionDataSourceRef.current || !entity.polyline)
            return;
        const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
        if (!positions)
            return;
        // Add a dashed white outline to indicate selection
        selectionDataSourceRef.current.entities.add({
            id: `selection-${entityId}`,
            polyline: {
                positions: positions,
                material: new Cesium.PolylineDashMaterialProperty({
                    color: Cesium.Color.WHITE,
                    dashLength: 16
                }),
                width: 4,
                clampToGround: true
            }
        });
    }, []);
    // Remove selection indicator for a stream
    const removeSelectionIndicator = useCallback((entityId) => {
        if (!selectionDataSourceRef.current)
            return;
        selectionDataSourceRef.current.entities.removeById(`selection-${entityId}`);
    }, []);
    // Clear all selection indicators
    const clearSelectionIndicators = useCallback(() => {
        if (selectionDataSourceRef.current) {
            selectionDataSourceRef.current.entities.removeAll();
        }
    }, []);
    const findAdjacentStreams = useCallback((selectedCoords) => {
        return findAdjacentStreamsFn(selectedCoords, loadedArtifacts);
    }, [loadedArtifacts]);
    // Update selectable stream highlighting
    const updateSelectableHighlights = useCallback((adjacentStreams) => {
        if (!selectableDataSourceRef.current)
            return;
        // Clear existing selectable highlights
        selectableDataSourceRef.current.entities.removeAll();
        // Add highlight for each adjacent stream
        adjacentStreams.forEach(({ entity }, entityId) => {
            var _a;
            if (!entity.polyline)
                return;
            const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
            if (!positions)
                return;
            selectableDataSourceRef.current.entities.add({
                id: `selectable-${entityId}`,
                polyline: {
                    positions: positions,
                    material: new Cesium.PolylineGlowMaterialProperty({
                        glowPower: 0.3,
                        color: new Cesium.CallbackProperty(() => {
                            const t = Date.now() / 1000;
                            const alpha = 0.35 + 0.25 * Math.sin(t * 2.5);
                            return Cesium.Color.CYAN.withAlpha(alpha);
                        }, false)
                    }),
                    width: 8,
                    clampToGround: true
                }
            });
        });
        // Update the set of selectable entity IDs
        setSelectableEntityIds(new Set(adjacentStreams.keys()));
    }, []);
    // Clear selectable highlights
    const clearSelectableHighlights = useCallback(() => {
        if (selectableDataSourceRef.current) {
            selectableDataSourceRef.current.entities.removeAll();
        }
        setSelectableEntityIds(new Set());
    }, []);
    // Handle click on map to detect stream entities. The Highlight Mode
    // gate only applies to CREATING a new highlight — clicking an
    // already-highlighted stream to edit it works any time, so users
    // don't have to flip the toggle on just to rename or recolour.
    const handleMapClick = useCallback((event) => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!pickedObject || !pickedObject.id)
            return false;
        const entity = pickedObject.id;
        // Check if this is a polyline entity
        if (!entity.polyline)
            return false;
        // Check if it's from a stream artifact
        const streamInfo = findStreamInfo(entity);
        if (!streamInfo)
            return false;
        const streamThreshold = extractStreamThreshold(streamInfo.artifact);
        const lineCoordinates = extractLineCoordinates(entity);
        // Already highlighted? Re-open the dialog in EDIT mode so the user
        // can change colour, width, or name. Snapshot the current highlight
        // attributes so cancel can restore them — preview-during-edit is
        // applied in place against the live highlight.
        const entityId = entity.id || `entity-${Date.now()}`;
        if (highlightedStreams.has(entityId)) {
            // Defensive cleanup: if the user somehow has an in-progress
            // create-flow selection (multi-select chrome on the map), drop it
            // before entering edit mode so we don't leave stray indicators.
            clearSelectionIndicators();
            clearSelectableHighlights();
            setSelectedStreams([]);
            const existing = highlightedStreams.get(entityId);
            editBaselineRef.current = {
                entityId,
                colorHex: existing.colorHex,
                userLineWidth: existing.userLineWidth,
                name: existing.name
            };
            setPendingClick({
                entity,
                streamThreshold: existing.streamThreshold,
                screenPosition: { x: event.position.x, y: event.position.y },
                artifactId: existing.artifactId,
                lineCoordinates: existing.lineCoordinates
            });
            return true;
        }
        // Creating a new highlight requires Highlight Mode to be on.
        // Editing an existing one (handled above) doesn't.
        if (!isActive)
            return false;
        // If constraint polygon is set, validate stream intersects it
        if (constraintPolygon && constraintPolygon.length >= 3 && entity.polyline) {
            const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
            if (positions && positions.length > 0) {
                const streamCoords = positions.map((pos) => {
                    const cartographic = Cesium.Cartographic.fromCartesian(pos);
                    return {
                        lat: Cesium.Math.toDegrees(cartographic.latitude),
                        lng: Cesium.Math.toDegrees(cartographic.longitude)
                    };
                });
                if (!doesPolylineIntersectPolygon(streamCoords, constraintPolygon)) {
                    return false;
                }
            }
        }
        const newStreamClick = {
            entity,
            streamThreshold,
            screenPosition: { x: event.position.x, y: event.position.y },
            artifactId: streamInfo.artifactId,
            lineCoordinates
        };
        // Check if already selected (toggle off if clicked again). Allow
        // removing any segment in the selection, not just the most recent —
        // critical after "Select all in this direction" auto-adds a chain;
        // otherwise the user can't undo individual segments. Contiguity is
        // not enforced on removal: the user is intentionally pruning, and
        // the next adjacent-stream search will work from whatever endpoints
        // remain.
        const alreadySelectedIndex = selectedStreams.findIndex(s => s.entity.id === entityId);
        if (alreadySelectedIndex >= 0) {
            removeSelectionIndicator(entityId);
            // Revert the preview styles on the de-selected stream.
            removeFromCreatePreviewRef.current(entityId);
            const newSelected = selectedStreams.filter((_, i) => i !== alreadySelectedIndex);
            setSelectedStreams(newSelected);
            if (newSelected.length > 0) {
                const coords = newSelected.map(s => s.lineCoordinates);
                const adjacent = findAdjacentStreams(coords);
                updateSelectableHighlights(adjacent);
            }
            else {
                clearSelectableHighlights();
                setPendingClick(null);
            }
            return true;
        }
        // If selection is in progress, only allow selecting adjacent (contiguous) streams
        if (selectedStreams.length > 0) {
            // Check if this entity is in the selectable set
            if (!selectableEntityIds.has(entityId)) {
                // Not adjacent - don't allow selection
                return false;
            }
            // Add to selection
            addSelectionIndicator(entityId, entity);
            // Live-preview the current dialog color/width on the newly added
            // stream so it visually joins the rest of the selection.
            applyCreatePreviewRef.current(entity, createPreviewStyleRef.current.colorHex, createPreviewStyleRef.current.lineWidth);
            const newSelected = [...selectedStreams, newStreamClick];
            setSelectedStreams(newSelected);
            // Update selectable highlights for the new selection
            const coords = newSelected.map(s => s.lineCoordinates);
            const adjacent = findAdjacentStreams(coords);
            updateSelectableHighlights(adjacent);
            return true;
        }
        // First click - open dialog and start selection.
        addSelectionIndicator(entityId, entity);
        // Apply the default-preview style to the first stream so the user
        // sees the color/width they're about to commit. Subsequent picks
        // in the dialog update this in place via previewColor /
        // previewLineWidth.
        applyCreatePreviewRef.current(entity, createPreviewStyleRef.current.colorHex, createPreviewStyleRef.current.lineWidth);
        setSelectedStreams([newStreamClick]);
        setPendingClick(newStreamClick);
        // Find and highlight adjacent streams
        const adjacent = findAdjacentStreams([lineCoordinates]);
        updateSelectableHighlights(adjacent);
        return true;
    }, [isActive, viewer, findStreamInfo, highlightedStreams, constraintPolygon, selectedStreams, selectableEntityIds, addSelectionIndicator, removeSelectionIndicator, findAdjacentStreams, updateSelectableHighlights, clearSelectableHighlights]);
    // Handle mouse move for hover detection. Always runs — the cursor
    // manager needs to know about hovered highlights even when Highlight
    // Mode is off, so existing highlights get the editable-cursor
    // affordance (matches the contour flow).
    const handleMapHover = useCallback((event) => {
        if (!viewer || viewer.isDestroyed()) {
            setHoveredStream(null);
            return false;
        }
        const pickedObject = viewer.scene.pick(event.endPosition);
        if (!pickedObject || !pickedObject.id) {
            setHoveredStream(null);
            return false;
        }
        const entity = pickedObject.id;
        if (!entity.polyline) {
            setHoveredStream(null);
            return false;
        }
        const streamInfo = findStreamInfo(entity);
        if (!streamInfo) {
            setHoveredStream(null);
            return false;
        }
        const entityId = entity.id || '';
        const isHighlighted = highlightedStreams.has(entityId);
        const highlight = highlightedStreams.get(entityId);
        const streamThreshold = isHighlighted && highlight
            ? highlight.streamThreshold
            : extractStreamThreshold(streamInfo.artifact);
        let hoverFraction;
        if (isHighlighted && highlight && highlight.lineCoordinates.length >= 2) {
            const ray = viewer.camera.getPickRay(event.endPosition);
            const terrainPos = ray ? viewer.scene.globe.pick(ray, viewer.scene) : null;
            if (terrainPos) {
                const cart = Cesium.Cartographic.fromCartesian(terrainPos);
                const hoverCoord = {
                    lat: Cesium.Math.toDegrees(cart.latitude),
                    lng: Cesium.Math.toDegrees(cart.longitude)
                };
                const coords = highlight.lineCoordinates;
                const candidates = [];
                let totalDist = 0;
                let cumulative = 0;
                for (let i = 0; i < coords.length - 1; i++) {
                    const segLen = haversineDistance(coords[i], coords[i + 1]);
                    if (segLen > 0) {
                        const dToStart = haversineDistance(hoverCoord, coords[i]);
                        const dToEnd = haversineDistance(hoverCoord, coords[i + 1]);
                        const t = Math.max(0, Math.min(1, (dToStart * dToStart - dToEnd * dToEnd + segLen * segLen) / (2 * segLen * segLen)));
                        const projLat = coords[i].lat + t * (coords[i + 1].lat - coords[i].lat);
                        const projLng = coords[i].lng + t * (coords[i + 1].lng - coords[i].lng);
                        const dToProj = haversineDistance(hoverCoord, { lat: projLat, lng: projLng });
                        candidates.push({ dToProj, fraction: cumulative + t * segLen });
                    }
                    cumulative += segLen;
                    totalDist += segLen;
                }
                if (candidates.length > 0 && totalDist > 0) {
                    const bestDist = Math.min(...candidates.map(c => c.dToProj));
                    // Accept any candidate within 25% of the closest segment's
                    // distance. With one winner the set is a singleton; with
                    // near-ties (S-curves), this lets the monotonic preference
                    // pick the segment that continues the previous trajectory.
                    const tieThreshold = bestDist * 1.25 + 0.5;
                    const tied = candidates.filter(c => c.dToProj <= tieThreshold);
                    const previous = lastHoverFractionByEntityRef.current.get(entityId);
                    let chosen;
                    if (previous !== undefined && tied.length > 1) {
                        const previousCumulative = previous * totalDist;
                        chosen = tied.reduce((best, c) => Math.abs(c.fraction - previousCumulative) < Math.abs(best.fraction - previousCumulative) ? c : best);
                    }
                    else {
                        chosen = tied.reduce((best, c) => c.dToProj < best.dToProj ? c : best);
                    }
                    hoverFraction = Math.max(0, Math.min(1, chosen.fraction / totalDist));
                    lastHoverFractionByEntityRef.current.set(entityId, hoverFraction);
                }
            }
        }
        else if (entityId) {
            lastHoverFractionByEntityRef.current.delete(entityId);
        }
        setHoveredStream({
            entityId,
            streamThreshold,
            screenPosition: { x: event.endPosition.x, y: event.endPosition.y },
            isHighlighted,
            bufferDistanceMeters: (highlight === null || highlight === void 0 ? void 0 : highlight.bufferDistanceMeters) || 0,
            bufferUnit: (highlight === null || highlight === void 0 ? void 0 : highlight.bufferUnit) || 'm',
            hoverFraction
        });
        return true;
    }, [viewer, findStreamInfo, highlightedStreams]);
    // Calculate buffer for a line
    const calculateBuffer = useCallback(async (lineCoordinates, bufferDistanceMeters) => {
        if (!projectId || lineCoordinates.length < 2) {
            throw new Error('Invalid input for buffer calculation');
        }
        const coords = lineCoordinates.map(c => ({
            lat: c.lat,
            lng: c.lng
        }));
        const result = await elevationService.calculateStreamBuffer(projectId, coords, bufferDistanceMeters);
        if (!result.success) {
            throw new Error(result.message || 'Buffer calculation failed');
        }
        return result.bufferPolygons;
    }, [projectId]);
    // Track all merged buffer entities (can be multiple for MultiPolygon)
    const mergedBufferEntitiesRef = useRef([]);
    const updateMergedBuffer = useCallback((streams) => {
        if (!buffersDataSourceRef.current)
            return;
        for (const entity of mergedBufferEntitiesRef.current) {
            buffersDataSourceRef.current.entities.remove(entity);
        }
        mergedBufferEntitiesRef.current = [];
        mergedBufferEntityRef.current = null;
        const result = createMergedBufferEntities(buffersDataSourceRef.current, streams);
        mergedBufferEntitiesRef.current = result.entities;
        mergedBufferEntityRef.current = result.primaryEntity;
    }, []);
    // Highlight a stream. Returns the built HighlightedStream so the
    // caller (confirmHighlight) can hand it back to the consumer for
    // immediate promotion — useful for sidestepping React's batched
    // state updates when the same caller wants to read the data back.
    const highlightStream = useCallback(async (entity, artifactId, streamThreshold, lineCoordinates, colorHex = DEFAULT_HIGHLIGHT_COLOR, 
    // distanceMeters is CANONICAL METERS — conversion from user units happens
    // at the UI boundary, never here. The unit rides along for display only.
    // (The old { distance } field meant user-units from the create flow and
    // meters from the restore flow; a 50 ft buffer was stored as 50 m.)
    bufferConfig, precomputedBufferPolygons, name, userLineWidth) => {
        var _a, _b, _c, _d, _e, _f;
        if (!viewer || viewer.isDestroyed() || !entity.polyline)
            return null;
        const entityId = entity.id || `entity-${Date.now()}`;
        const highlightColor = Cesium.Color.fromCssColorString(colorHex);
        // Snapshot the source polyline's exact properties so unhighlight can
        // restore them verbatim. Extracting just the color (and rewrapping)
        // loses any non-ColorMaterialProperty styling — that was the cause
        // of streams looking "deleted" after delete-from-unsaved.
        const originalMaterialProperty = entity.polyline.material;
        const originalWidthProperty = entity.polyline.width;
        const originalShowProperty = entity.polyline.show;
        const originalClampToGround = entity.polyline.clampToGround;
        const originalArcType = entity.polyline.arcType;
        const originalEntityShow = entity.show === undefined ? true : (_c = (_b = (_a = entity.show).getValue) === null || _b === void 0 ? void 0 : _b.call(_a, Cesium.JulianDate.now())) !== null && _c !== void 0 ? _c : true;
        // Keep the legacy numeric width + color refs for camera-aware scaling.
        const originalWidth = ((_d = entity.polyline.width) === null || _d === void 0 ? void 0 : _d.getValue(Cesium.JulianDate.now())) || 2;
        const originalMaterial = (_e = entity.polyline.material) === null || _e === void 0 ? void 0 : _e.getValue(Cesium.JulianDate.now());
        const originalColor = (originalMaterial === null || originalMaterial === void 0 ? void 0 : originalMaterial.color) || Cesium.Color.BLUE;
        // Apply highlight style — base width is user-set (slider) or default.
        // getHighlightWidth scales it up for zoom so far-out streams stay
        // visible.
        const baseWidth = userLineWidth !== null && userLineWidth !== void 0 ? userLineWidth : DEFAULT_HIGHLIGHT_LINE_WIDTH;
        const cameraHeight = viewer.camera.positionCartographic.height;
        const highlightWidth = getHighlightWidth(cameraHeight, baseWidth);
        entity.polyline.material = new Cesium.ColorMaterialProperty(highlightColor);
        entity.polyline.width = new Cesium.ConstantProperty(highlightWidth);
        entity.polyline.clampToGround = new Cesium.ConstantProperty(true);
        // Clamped polylines require GEODESIC or RHUMB — Cesium throws on
        // render if arcType is NONE (which KML/GeoJSON loaders often set).
        entity.polyline.arcType = new Cesium.ConstantProperty(Cesium.ArcType.GEODESIC);
        // Calculate buffer if requested (or use precomputed)
        // Note: Individual buffer entities are not created here; merged buffer is updated separately
        let bufferPolygons = [];
        let bufferDistanceMeters = 0;
        let bufferUnit = 'm';
        if (precomputedBufferPolygons && precomputedBufferPolygons.length > 0) {
            // Use precomputed buffer polygons (from create commit, scope restore or
            // shared highlight)
            bufferPolygons = precomputedBufferPolygons;
            if (bufferConfig) {
                bufferDistanceMeters = bufferConfig.distanceMeters;
                bufferUnit = bufferConfig.unit;
            }
        }
        else if (bufferConfig && bufferConfig.distanceMeters > 0) {
            setIsCalculatingBuffer(true);
            try {
                bufferDistanceMeters = bufferConfig.distanceMeters;
                bufferUnit = bufferConfig.unit;
                bufferPolygons = await calculateBuffer(lineCoordinates, bufferDistanceMeters);
            }
            catch (error) {
                console.error('Failed to calculate buffer:', error);
            }
            finally {
                setIsCalculatingBuffer(false);
            }
        }
        let totalDistanceMeters = 0;
        for (let i = 0; i < lineCoordinates.length - 1; i++) {
            totalDistanceMeters += haversineDistance(lineCoordinates[i], lineCoordinates[i + 1]);
        }
        // Store highlight info (bufferEntity is null - merged buffer is managed separately)
        const highlightName = name || `${streamThreshold} Stream`;
        const highlightInfo = {
            entityId,
            entity,
            name: highlightName,
            streamThreshold,
            originalWidth,
            originalColor: originalColor.clone(),
            originalMaterialProperty,
            originalWidthProperty,
            originalShowProperty,
            originalClampToGround,
            originalArcType,
            originalEntityShow,
            highlightColor: highlightColor.clone(),
            colorHex,
            userLineWidth: userLineWidth !== null && userLineWidth !== void 0 ? userLineWidth : DEFAULT_HIGHLIGHT_LINE_WIDTH,
            artifactId,
            bufferDistanceMeters,
            bufferUnit,
            bufferEntity: null,
            bufferPolygons,
            lineCoordinates,
            totalDistanceMeters
        };
        setHighlightedStreams((prev) => {
            const newMap = new Map(prev);
            newMap.set(entityId, highlightInfo);
            return newMap;
        });
        // Clone entity to highlights data source for visibility override
        if (highlightsDataSourceRef.current) {
            const positions = (_f = entity.polyline.positions) === null || _f === void 0 ? void 0 : _f.getValue(Cesium.JulianDate.now());
            if (positions) {
                const existing = highlightsDataSourceRef.current.entities.getById(entityId);
                if (existing) {
                    highlightsDataSourceRef.current.entities.remove(existing);
                }
                highlightsDataSourceRef.current.entities.add({
                    id: entityId,
                    polyline: {
                        positions: positions,
                        material: new Cesium.ColorMaterialProperty(highlightColor),
                        width: highlightWidth,
                        clampToGround: true
                    },
                    show: false
                });
            }
        }
        return highlightInfo;
    }, [viewer, calculateBuffer]);
    // Unhighlight a stream. `highlightOverride` lets the save-to-folder
    // flow pass the just-committed HighlightedStream from confirmHighlight's
    // snapshot — bypasses a stale React-state read when cleanup runs in the
    // same tick as the commit.
    const unhighlightStream = useCallback((entityId, highlightOverride) => {
        const highlight = highlightOverride !== null && highlightOverride !== void 0 ? highlightOverride : highlightedStreams.get(entityId);
        if (!highlight)
            return;
        // Restore original styles verbatim. Assigning `undefined` to a
        // polyline property is valid in Cesium (clears it) and matches the
        // pre-highlight state if the loader hadn't set the property either.
        if (highlight.entity.polyline) {
            highlight.entity.polyline.material = highlight.originalMaterialProperty;
            highlight.entity.polyline.width = highlight.originalWidthProperty;
            highlight.entity.polyline.show = highlight.originalShowProperty;
            highlight.entity.polyline.clampToGround = highlight.originalClampToGround;
            highlight.entity.polyline.arcType = highlight.originalArcType;
        }
        highlight.entity.show = highlight.originalEntityShow;
        // Note: Merged buffer entity is updated automatically via useEffect when highlightedStreams changes
        // Remove from highlights data source
        if (highlightsDataSourceRef.current) {
            const highlightEntity = highlightsDataSourceRef.current.entities.getById(entityId);
            if (highlightEntity) {
                highlightsDataSourceRef.current.entities.remove(highlightEntity);
            }
        }
        // Remove synthetic entity (server-artifact-edit only).
        if (synthDataSourceRef.current) {
            const synthEntity = synthDataSourceRef.current.entities.getById(entityId);
            if (synthEntity) {
                synthDataSourceRef.current.entities.remove(synthEntity);
            }
        }
        setHighlightedStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(entityId);
            return newMap;
        });
    }, [highlightedStreams]);
    // Find all contiguous highlighted streams connected to a starting stream
    const findContiguousHighlighted = useCallback((startEntityId) => {
        const connected = new Set();
        const toVisit = [startEntityId];
        while (toVisit.length > 0) {
            const currentId = toVisit.pop();
            if (connected.has(currentId))
                continue;
            const current = highlightedStreams.get(currentId);
            if (!current)
                continue;
            connected.add(currentId);
            // Get endpoints of current stream
            const currentCoords = current.lineCoordinates;
            if (currentCoords.length < 2)
                continue;
            const currentStart = currentCoords[0];
            const currentEnd = currentCoords[currentCoords.length - 1];
            // Find other highlighted streams that share an endpoint
            highlightedStreams.forEach((other, otherId) => {
                if (connected.has(otherId))
                    return;
                const otherCoords = other.lineCoordinates;
                if (otherCoords.length < 2)
                    return;
                const otherStart = otherCoords[0];
                const otherEnd = otherCoords[otherCoords.length - 1];
                // Check if any endpoints match (within threshold)
                const isConnected = haversineDistance(currentStart, otherStart) < CONNECTION_THRESHOLD_METERS ||
                    haversineDistance(currentStart, otherEnd) < CONNECTION_THRESHOLD_METERS ||
                    haversineDistance(currentEnd, otherStart) < CONNECTION_THRESHOLD_METERS ||
                    haversineDistance(currentEnd, otherEnd) < CONNECTION_THRESHOLD_METERS;
                if (isConnected) {
                    toVisit.push(otherId);
                }
            });
        }
        return connected;
    }, [highlightedStreams]);
    // Get all contiguous groups of highlighted streams
    // Returns an array of groups, where each group is a Set of entity IDs
    const getAllContiguousGroups = useCallback(() => {
        const visited = new Set();
        const groups = [];
        highlightedStreams.forEach((highlight, entityId) => {
            if (visited.has(entityId))
                return;
            const group = findContiguousHighlighted(entityId);
            group.forEach(id => visited.add(id));
            // Use the first entity ID (alphabetically sorted for consistency) as the group ID
            const sortedIds = Array.from(group).sort();
            const groupId = `stream-group-${sortedIds[0]}`;
            // Use the first highlight's properties for the group
            groups.push({
                groupId,
                entityIds: group,
                highlight
            });
        });
        return groups;
    }, [highlightedStreams, findContiguousHighlighted]);
    // Handle double-click to unhighlight contiguous selection. Always
    // available — if you can see the highlight, you can remove it; no
    // Highlight Mode required (matches the click-to-edit affordance).
    const handleMapDoubleClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!pickedObject || !pickedObject.id)
            return false;
        const entity = pickedObject.id;
        const entityId = entity.id;
        if (!entityId || !highlightedStreams.has(entityId))
            return false;
        // Find all contiguous highlighted streams and unhighlight them all
        const contiguousIds = findContiguousHighlighted(entityId);
        contiguousIds.forEach(id => unhighlightStream(id));
        return true;
    }, [viewer, highlightedStreams, findContiguousHighlighted, unhighlightStream]);
    // Unhighlight all streams
    const unhighlightAll = useCallback(() => {
        highlightedStreams.forEach((highlight) => {
            if (highlight.entity.polyline) {
                highlight.entity.polyline.material = highlight.originalMaterialProperty;
                highlight.entity.polyline.width = highlight.originalWidthProperty;
                highlight.entity.polyline.show = highlight.originalShowProperty;
                highlight.entity.polyline.clampToGround = highlight.originalClampToGround;
                highlight.entity.polyline.arcType = highlight.originalArcType;
            }
            highlight.entity.show = highlight.originalEntityShow;
        });
        if (highlightsDataSourceRef.current) {
            highlightsDataSourceRef.current.entities.removeAll();
        }
        if (buffersDataSourceRef.current) {
            buffersDataSourceRef.current.entities.removeAll();
        }
        // Clear merged buffer refs
        mergedBufferEntityRef.current = null;
        mergedBufferEntitiesRef.current = [];
        setHighlightedStreams(new Map());
    }, [highlightedStreams]);
    // Apply a colour to every entity in a contiguous group — used by the
    // edit-mode preview path and by cancel-to-restore. Updates the source
    // polyline, the clone (in highlightsDataSourceRef), and the merged
    // buffer entities' alpha-faded fill so everything stays in sync.
    const updateGroupColor = useCallback((entityIds, colorHex) => {
        const newColor = Cesium.Color.fromCssColorString(colorHex);
        entityIds.forEach(id => {
            const highlight = highlightedStreams.get(id);
            if (!highlight)
                return;
            if (highlight.entity.polyline) {
                highlight.entity.polyline.material = new Cesium.ColorMaterialProperty(newColor);
            }
            if (highlightsDataSourceRef.current) {
                const clone = highlightsDataSourceRef.current.entities.getById(id);
                if (clone === null || clone === void 0 ? void 0 : clone.polyline) {
                    clone.polyline.material = new Cesium.ColorMaterialProperty(newColor);
                }
            }
        });
        mergedBufferEntitiesRef.current.forEach(entity => {
            if (entity.polygon) {
                entity.polygon.material = new Cesium.ColorMaterialProperty(newColor.withAlpha(0.3));
            }
        });
        setHighlightedStreams(prev => {
            const newMap = new Map(prev);
            entityIds.forEach(id => {
                const h = newMap.get(id);
                if (h)
                    newMap.set(id, { ...h, colorHex, highlightColor: newColor.clone() });
            });
            return newMap;
        });
    }, [highlightedStreams]);
    // Apply a base line width to every entity in a contiguous group.
    // getHighlightWidth multiplies it for the current zoom so the
    // on-screen thickness scales like the rest of the highlight.
    const updateGroupLineWidth = useCallback((entityIds, baseWidth) => {
        if (!viewer || viewer.isDestroyed())
            return;
        const cameraHeight = viewer.camera.positionCartographic.height;
        const scaledWidth = getHighlightWidth(cameraHeight, baseWidth);
        entityIds.forEach(id => {
            const highlight = highlightedStreams.get(id);
            if (!highlight)
                return;
            if (highlight.entity.polyline) {
                highlight.entity.polyline.width = new Cesium.ConstantProperty(scaledWidth);
            }
            if (highlightsDataSourceRef.current) {
                const clone = highlightsDataSourceRef.current.entities.getById(id);
                if (clone === null || clone === void 0 ? void 0 : clone.polyline) {
                    clone.polyline.width = new Cesium.ConstantProperty(scaledWidth);
                }
            }
        });
        setHighlightedStreams(prev => {
            const newMap = new Map(prev);
            entityIds.forEach(id => {
                const h = newMap.get(id);
                if (h)
                    newMap.set(id, { ...h, userLineWidth: baseWidth });
            });
            return newMap;
        });
    }, [viewer, highlightedStreams]);
    // Apply preview color/width to a source entity's polyline. Captures
    // the original on first touch so cancelHighlight can revert without
    // having to ask the loader for it again.
    const applyCreatePreviewToEntity = useCallback((entity, colorHex, lineWidthBase) => {
        if (!entity.polyline || !viewer || viewer.isDestroyed())
            return;
        const id = entity.id || '';
        if (id && !createPreviewBaselineRef.current.has(id)) {
            createPreviewBaselineRef.current.set(id, {
                entity,
                material: entity.polyline.material,
                width: entity.polyline.width,
                show: entity.polyline.show,
                clampToGround: entity.polyline.clampToGround,
                arcType: entity.polyline.arcType,
            });
        }
        const color = Cesium.Color.fromCssColorString(colorHex);
        const scaled = getHighlightWidth(viewer.camera.positionCartographic.height, lineWidthBase);
        entity.polyline.material = new Cesium.ColorMaterialProperty(color);
        entity.polyline.width = new Cesium.ConstantProperty(scaled);
        entity.polyline.clampToGround = new Cesium.ConstantProperty(true);
        entity.polyline.arcType = new Cesium.ConstantProperty(Cesium.ArcType.GEODESIC);
    }, [viewer]);
    // Revert every entity in the create-mode preview to its captured
    // baseline. Safe to call multiple times.
    const clearCreatePreview = useCallback(() => {
        createPreviewBaselineRef.current.forEach((baseline) => {
            if (baseline.entity.polyline) {
                baseline.entity.polyline.material = baseline.material;
                baseline.entity.polyline.width = baseline.width;
                baseline.entity.polyline.show = baseline.show;
                baseline.entity.polyline.clampToGround = baseline.clampToGround;
                baseline.entity.polyline.arcType = baseline.arcType;
            }
        });
        createPreviewBaselineRef.current.clear();
        createPreviewStyleRef.current = {
            colorHex: DEFAULT_HIGHLIGHT_COLOR,
            lineWidth: DEFAULT_HIGHLIGHT_LINE_WIDTH,
        };
    }, []);
    // Revert + drop a single entity from the create-mode preview (used
    // when the user toggles a stream out of the multi-select).
    const removeFromCreatePreview = useCallback((entityId) => {
        const baseline = createPreviewBaselineRef.current.get(entityId);
        if (!baseline)
            return;
        if (baseline.entity.polyline) {
            baseline.entity.polyline.material = baseline.material;
            baseline.entity.polyline.width = baseline.width;
            baseline.entity.polyline.show = baseline.show;
            baseline.entity.polyline.clampToGround = baseline.clampToGround;
            baseline.entity.polyline.arcType = baseline.arcType;
        }
        createPreviewBaselineRef.current.delete(entityId);
    }, []);
    // Sync forward refs so handleMapClick (declared earlier) can call
    // these helpers.
    applyCreatePreviewRef.current = applyCreatePreviewToEntity;
    removeFromCreatePreviewRef.current = removeFromCreatePreview;
    // Live preview hook for the dialog's colour picker. Edit mode mutates
    // the committed highlight in place; create mode mutates the source
    // polyline (baselines captured per entity for revert on cancel).
    const previewColor = useCallback((colorHex) => {
        if (editBaselineRef.current) {
            const group = findContiguousHighlighted(editBaselineRef.current.entityId);
            updateGroupColor(group, colorHex);
            return;
        }
        if (selectedStreams.length === 0)
            return;
        createPreviewStyleRef.current = {
            ...createPreviewStyleRef.current,
            colorHex,
        };
        for (const s of selectedStreams) {
            applyCreatePreviewToEntity(s.entity, colorHex, createPreviewStyleRef.current.lineWidth);
        }
    }, [findContiguousHighlighted, updateGroupColor, selectedStreams, applyCreatePreviewToEntity]);
    // Live preview hook for the dialog's line-width slider.
    const previewLineWidth = useCallback((baseWidth) => {
        if (editBaselineRef.current) {
            const group = findContiguousHighlighted(editBaselineRef.current.entityId);
            updateGroupLineWidth(group, baseWidth);
            return;
        }
        if (selectedStreams.length === 0)
            return;
        createPreviewStyleRef.current = {
            ...createPreviewStyleRef.current,
            lineWidth: baseWidth,
        };
        for (const s of selectedStreams) {
            applyCreatePreviewToEntity(s.entity, createPreviewStyleRef.current.colorHex, baseWidth);
        }
    }, [findContiguousHighlighted, updateGroupLineWidth, selectedStreams, applyCreatePreviewToEntity]);
    // Clear any in-flight buffer-preview entities + state. Safe to call
    // anytime; no-op if no preview is active.
    const clearBufferPreview = useCallback(() => {
        if (bufferPreviewDataSourceRef.current) {
            bufferPreviewDataSourceRef.current.entities.removeAll();
        }
        bufferPreviewPolygonsRef.current = new Map();
        bufferPreviewMetaRef.current = null;
    }, []);
    // Fetch a buffer for the current edit context (selectedStreams in
    // create mode, the edited group in edit mode), render it as a preview
    // polygon on the map without committing the highlight. Lets the user
    // see the geometry before clicking Save — mirrors the path tool's
    // "Add Buffer → Apply" flow.
    // Returns the polygons keyed by entityId, or null if anything failed
    // and nothing was rendered.
    const previewBuffer = useCallback(async (distance, unit) => {
        if (!projectId || distance <= 0)
            return null;
        const distanceMeters = unit === 'ft' ? distance * 0.3048 : distance;
        const segments = [];
        if (editBaselineRef.current) {
            const group = findContiguousHighlighted(editBaselineRef.current.entityId);
            group.forEach(id => {
                const h = highlightedStreams.get(id);
                if (h)
                    segments.push({ entityId: id, coords: h.lineCoordinates });
            });
        }
        else {
            for (const s of selectedStreams) {
                const id = s.entity.id || '';
                if (id)
                    segments.push({ entityId: id, coords: s.lineCoordinates });
            }
        }
        if (segments.length === 0)
            return null;
        setIsCalculatingBuffer(true);
        try {
            const results = await Promise.all(segments.map(async (seg) => {
                try {
                    const r = await elevationService.calculateStreamBuffer(projectId, seg.coords.map(c => ({ lat: c.lat, lng: c.lng })), distanceMeters);
                    if (r.success) {
                        return { id: seg.entityId, buffer: r.bufferPolygons };
                    }
                }
                catch (e) {
                    console.warn(`Preview buffer failed for ${seg.entityId}:`, e);
                }
                return { id: seg.entityId, buffer: [] };
            }));
            // Drop any in-flight preview entities BEFORE rendering new ones.
            if (bufferPreviewDataSourceRef.current) {
                bufferPreviewDataSourceRef.current.entities.removeAll();
            }
            // Stash polygons + meta so confirmHighlight can reuse them.
            const polygonMap = new Map();
            for (const r of results) {
                if (r.buffer.length > 0)
                    polygonMap.set(r.id, r.buffer);
            }
            bufferPreviewPolygonsRef.current = polygonMap;
            bufferPreviewMetaRef.current = { distance, unit };
            // Render preview polygons. For multi-segment, we union and draw
            // the merged shape; for a single segment we render the raw rings.
            // Reuses streamBufferMerge by synthesizing pseudo-highlights —
            // streamBufferMerge only reads bufferPolygons + highlightColor.
            if (bufferPreviewDataSourceRef.current && polygonMap.size > 0) {
                // Determine the preview color: edit mode → current group's
                // color; create mode → the source layer's likely color (we
                // fall back to the deep-sky default since the dialog hasn't
                // committed a colour yet).
                let previewColor = null;
                if (editBaselineRef.current) {
                    const cur = highlightedStreams.get(editBaselineRef.current.entityId);
                    if (cur)
                        previewColor = cur.highlightColor;
                }
                if (!previewColor) {
                    previewColor = Cesium.Color.fromCssColorString(DEFAULT_HIGHLIGHT_COLOR);
                }
                const pseudoMap = new Map();
                polygonMap.forEach((polys, id) => {
                    pseudoMap.set(id, {
                        bufferPolygons: polys,
                        highlightColor: previewColor,
                    });
                });
                createMergedBufferEntities(bufferPreviewDataSourceRef.current, pseudoMap);
            }
            return polygonMap;
        }
        finally {
            setIsCalculatingBuffer(false);
        }
    }, [projectId, findContiguousHighlighted, highlightedStreams, selectedStreams]);
    // Open the highlight dialog over a server-saved artifact, with the
    // saved geometry synthesised into the in-memory highlight system so
    // the existing edit/preview/save machinery just works. Used by the
    // right-click → Edit flow from a folder. The serverArtifactId is the
    // user_artifact row id; on Save, the promotion handler's editingState
    // path will route to userFolderService.updateArtifact for that row.
    const startEditFromArtifact = useCallback((geometry, name, color, serverArtifactId, viewOnly = false) => {
        var _a, _b, _c, _d;
        if (!viewer || viewer.isDestroyed() || !synthDataSourceRef.current)
            return null;
        // Coerce legacy flat-ring polygons into the new shape: a single
        // exterior, no holes. Older saved artifacts only have buffer_polygon
        // (Coordinate[]); newer ones have buffer_polygons (BufferPolygon[]).
        const readBufferPolygons = (src) => {
            if (Array.isArray(src === null || src === void 0 ? void 0 : src.bufferPolygons))
                return src.bufferPolygons;
            if (Array.isArray(src === null || src === void 0 ? void 0 : src.buffer_polygons))
                return src.buffer_polygons;
            const legacy = Array.isArray(src === null || src === void 0 ? void 0 : src.bufferPolygon)
                ? src.bufferPolygon
                : (Array.isArray(src === null || src === void 0 ? void 0 : src.buffer_polygon) ? src.buffer_polygon : null);
            if (legacy && legacy.length >= 3) {
                return [{ exterior: legacy, holes: [] }];
            }
            return [];
        };
        const segments = [];
        if (geometry.is_multi_segment && Array.isArray(geometry.segments)) {
            for (let i = 0; i < geometry.segments.length; i++) {
                const s = geometry.segments[i];
                segments.push({
                    entityId: s.entityId || `synth-${serverArtifactId}-${i}`,
                    coords: Array.isArray(s.lineCoordinates) ? s.lineCoordinates : [],
                    bufferPolygons: readBufferPolygons(s),
                });
            }
        }
        else {
            segments.push({
                entityId: geometry.entity_id || `synth-${serverArtifactId}-0`,
                coords: Array.isArray(geometry.line_coordinates) ? geometry.line_coordinates : [],
                bufferPolygons: readBufferPolygons(geometry),
            });
        }
        if (segments.length === 0 || segments[0].coords.length < 2)
            return null;
        const streamThreshold = String(geometry.stream_threshold || 'Unknown');
        const bufferDistanceMeters = (_b = (_a = geometry.buffer_settings) === null || _a === void 0 ? void 0 : _a.distance_meters) !== null && _b !== void 0 ? _b : 0;
        const bufferUnit = (_d = (_c = geometry.buffer_settings) === null || _c === void 0 ? void 0 : _c.unit) !== null && _d !== void 0 ? _d : DEFAULT_BUFFER_UNIT;
        const highlightColor = Cesium.Color.fromCssColorString(color);
        const cameraHeight = viewer.camera.positionCartographic.height;
        const baseWidth = DEFAULT_HIGHLIGHT_LINE_WIDTH;
        const scaledWidth = getHighlightWidth(cameraHeight, baseWidth);
        for (const seg of segments) {
            const positions = seg.coords.map(c => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
            // Re-create the synth entity if it already exists (rare but
            // possible if the user reopens an in-progress edit).
            const existing = synthDataSourceRef.current.entities.getById(seg.entityId);
            if (existing)
                synthDataSourceRef.current.entities.remove(existing);
            const synthEntity = synthDataSourceRef.current.entities.add({
                id: seg.entityId,
                polyline: {
                    positions,
                    material: new Cesium.ColorMaterialProperty(highlightColor),
                    width: scaledWidth,
                    clampToGround: true,
                    arcType: Cesium.ArcType.GEODESIC,
                },
            });
            const info = {
                entityId: seg.entityId,
                entity: synthEntity,
                name,
                streamThreshold,
                originalWidth: baseWidth,
                originalColor: highlightColor.clone(),
                // No source layer to restore — leave originals undefined; on
                // unhighlight we remove the synth entity instead.
                originalMaterialProperty: undefined,
                originalWidthProperty: undefined,
                originalShowProperty: undefined,
                originalClampToGround: undefined,
                originalArcType: undefined,
                originalEntityShow: true,
                highlightColor: highlightColor.clone(),
                colorHex: color,
                userLineWidth: baseWidth,
                artifactId: serverArtifactId,
                bufferDistanceMeters,
                bufferUnit,
                bufferEntity: null,
                bufferPolygons: seg.bufferPolygons,
                lineCoordinates: seg.coords,
                totalDistanceMeters: 0,
            };
            setHighlightedStreams(prev => {
                const newMap = new Map(prev);
                newMap.set(seg.entityId, info);
                return newMap;
            });
        }
        // Pick the first segment as the dialog anchor + baseline. Sort the
        // entity IDs so the resulting groupId is stable (matches the
        // promotion path's `stream-group-${sortedIds[0]}` formula).
        const sortedIds = segments.map(s => s.entityId).sort();
        const anchorId = sortedIds[0];
        const anchorSeg = segments.find(s => s.entityId === anchorId);
        const anchorEntity = synthDataSourceRef.current.entities.getById(anchorId);
        if (viewOnly) {
            viewModeAnchorRef.current = { entityId: anchorId };
            editBaselineRef.current = null;
            setIsViewMode(true);
        }
        else {
            editBaselineRef.current = {
                entityId: anchorId,
                colorHex: color,
                userLineWidth: baseWidth,
                name,
            };
            viewModeAnchorRef.current = null;
            setIsViewMode(false);
        }
        setPendingClick({
            entity: anchorEntity,
            streamThreshold,
            screenPosition: { x: window.innerWidth / 2 - 170, y: window.innerHeight / 2 - 110 },
            artifactId: serverArtifactId,
            lineCoordinates: anchorSeg.coords,
        });
        return `stream-group-${anchorId}`;
    }, [viewer]);
    // View → edit promotion. The anchor highlight is already loaded; flip
    // the baseline ref so confirmHighlight takes the edit branch and the
    // dialog re-renders with full controls.
    const promoteToEdit = useCallback(() => {
        var _a;
        const anchor = viewModeAnchorRef.current;
        if (!anchor)
            return;
        const highlight = highlightedStreams.get(anchor.entityId);
        if (!highlight)
            return;
        editBaselineRef.current = {
            entityId: anchor.entityId,
            colorHex: highlight.colorHex,
            userLineWidth: (_a = highlight.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH,
            name: highlight.name,
        };
        viewModeAnchorRef.current = null;
        setIsViewMode(false);
    }, [highlightedStreams]);
    // Confirm highlight from dialog. Two modes:
    //  - EDIT: clicked an already-highlighted stream. Colour/width were
    //    applied in place via previewColor/previewLineWidth; here we just
    //    persist the final values across the whole contiguous group and
    //    clear the baseline so cancel can't revert post-confirm.
    //  - CREATE: existing multi-select flow. Computes per-segment buffers
    //    and applies the highlight to every selected stream.
    const confirmHighlight = useCallback(async (colorHex = DEFAULT_HIGHLIGHT_COLOR, bufferConfig, name, lineWidth) => {
        var _a, _b, _c, _d, _e;
        // Edit-mode branch: persist final colour/width/name across the
        // contiguous group. Buffer is the one field that needs an async
        // recompute when the user changes it — the per-segment polygons
        // are computed server-side, then the merged-buffer entity is
        // refreshed from them.
        if (editBaselineRef.current) {
            const editId = editBaselineRef.current.entityId;
            const group = findContiguousHighlighted(editId);
            updateGroupColor(group, colorHex);
            if (lineWidth !== undefined) {
                updateGroupLineWidth(group, lineWidth);
            }
            if (name !== undefined) {
                setHighlightedStreams(prev => {
                    const newMap = new Map(prev);
                    group.forEach(id => {
                        const h = newMap.get(id);
                        if (h)
                            newMap.set(id, { ...h, name });
                    });
                    return newMap;
                });
            }
            // Buffer change detection. We compare the requested buffer (in
            // meters) against the existing stream's stored bufferDistanceMeters.
            // A 0.1 m epsilon absorbs rounding from ft↔m round-trips.
            const firstId = Array.from(group)[0];
            const existing = firstId ? highlightedStreams.get(firstId) : undefined;
            const currentMeters = (_a = existing === null || existing === void 0 ? void 0 : existing.bufferDistanceMeters) !== null && _a !== void 0 ? _a : 0;
            const targetMeters = bufferConfig && bufferConfig.distance > 0
                ? (bufferConfig.unit === 'ft' ? bufferConfig.distance * 0.3048 : bufferConfig.distance)
                : 0;
            const targetUnit = (_c = (_b = bufferConfig === null || bufferConfig === void 0 ? void 0 : bufferConfig.unit) !== null && _b !== void 0 ? _b : existing === null || existing === void 0 ? void 0 : existing.bufferUnit) !== null && _c !== void 0 ? _c : 'ft';
            const bufferChanged = Math.abs(currentMeters - targetMeters) > 0.1;
            let bufferResults = [];
            if (bufferChanged) {
                setIsCalculatingBuffer(true);
                try {
                    const entityIds = Array.from(group);
                    // Reuse preview polygons if the user already clicked Apply
                    // for this same distance/unit. Otherwise fetch fresh.
                    const editPreviewPolygons = bufferPreviewPolygonsRef.current;
                    const editPreviewMatches = bufferConfig &&
                        editPreviewPolygons.size > 0 &&
                        bufferPreviewMetaRef.current &&
                        Math.abs(bufferPreviewMetaRef.current.distance - bufferConfig.distance) < 0.001 &&
                        bufferPreviewMetaRef.current.unit === bufferConfig.unit;
                    if (editPreviewMatches) {
                        bufferResults = entityIds.map(id => {
                            var _a;
                            return ({
                                id,
                                buffer: (_a = editPreviewPolygons.get(id)) !== null && _a !== void 0 ? _a : [],
                            });
                        });
                    }
                    else {
                        bufferResults = await Promise.all(entityIds.map(async (id) => {
                            const h = highlightedStreams.get(id);
                            if (!h)
                                return { id, buffer: [] };
                            if (targetMeters <= 0)
                                return { id, buffer: [] };
                            try {
                                const r = await elevationService.calculateStreamBuffer(projectId, h.lineCoordinates.map(c => ({ lat: c.lat, lng: c.lng })), targetMeters);
                                if (r.success) {
                                    return { id, buffer: r.bufferPolygons };
                                }
                            }
                            catch (e) {
                                console.warn(`Failed to recompute buffer for ${id}:`, e);
                            }
                            return { id, buffer: [] };
                        }));
                    }
                    setHighlightedStreams(prev => {
                        const newMap = new Map(prev);
                        for (const { id, buffer } of bufferResults) {
                            const h = newMap.get(id);
                            if (h) {
                                newMap.set(id, {
                                    ...h,
                                    bufferDistanceMeters: targetMeters,
                                    bufferUnit: targetUnit,
                                    bufferPolygons: buffer
                                });
                            }
                        }
                        return newMap;
                    });
                }
                finally {
                    setIsCalculatingBuffer(false);
                }
            }
            // Build a snapshot reflecting the just-applied edits. The caller
            // uses this to promote without re-reading React state (which is
            // still pre-render at this point — see Bug 1).
            const editSnapshot = new Map();
            const editColor = Cesium.Color.fromCssColorString(colorHex);
            const editTargetMeters = bufferChanged ? targetMeters : currentMeters;
            const editTargetUnit = bufferChanged ? targetUnit : ((_d = existing === null || existing === void 0 ? void 0 : existing.bufferUnit) !== null && _d !== void 0 ? _d : 'ft');
            const editBufferByEntity = new Map();
            for (const r of bufferResults)
                editBufferByEntity.set(r.id, r.buffer);
            for (const id of group) {
                const cur = highlightedStreams.get(id);
                if (!cur)
                    continue;
                editSnapshot.set(id, {
                    ...cur,
                    colorHex,
                    highlightColor: editColor.clone(),
                    userLineWidth: lineWidth !== null && lineWidth !== void 0 ? lineWidth : cur.userLineWidth,
                    name: name !== null && name !== void 0 ? name : cur.name,
                    bufferDistanceMeters: editTargetMeters,
                    bufferUnit: editTargetUnit,
                    bufferPolygons: bufferChanged
                        ? ((_e = editBufferByEntity.get(id)) !== null && _e !== void 0 ? _e : cur.bufferPolygons)
                        : cur.bufferPolygons,
                });
            }
            const sortedEditIds = Array.from(group).sort();
            const editGroupId = sortedEditIds.length > 0 ? `stream-group-${sortedEditIds[0]}` : null;
            editBaselineRef.current = null;
            setPendingClick(null);
            // The preview entities (if any) are now stale — the committed
            // merged buffer takes over.
            clearBufferPreview();
            return editGroupId ? { groupId: editGroupId, snapshot: editSnapshot } : null;
        }
        if (selectedStreams.length === 0)
            return null;
        const segments = selectedStreams.map(s => s.lineCoordinates);
        setIsCalculatingBuffer(true);
        try {
            let bufferDistanceMeters = 0;
            if (bufferConfig && bufferConfig.distance > 0) {
                bufferDistanceMeters = bufferConfig.unit === 'ft'
                    ? bufferConfig.distance * 0.3048
                    : bufferConfig.distance;
            }
            // Always compute per-segment buffers — updateMergedBuffer handles merging
            const segmentBuffers = new Map();
            // Reuse polygons from the in-flight preview when available — avoids
            // a duplicate server roundtrip on Save when the user already
            // clicked Apply. Preview was keyed by entityId; map to segment
            // index via selectedStreams.
            const previewPolygons = bufferPreviewPolygonsRef.current;
            const previewMatchesRequest = bufferConfig &&
                previewPolygons.size > 0 &&
                bufferPreviewMetaRef.current &&
                Math.abs(bufferPreviewMetaRef.current.distance - bufferConfig.distance) < 0.001 &&
                bufferPreviewMetaRef.current.unit === bufferConfig.unit;
            if (previewMatchesRequest) {
                selectedStreams.forEach((s, idx) => {
                    const id = s.entity.id || '';
                    const cached = previewPolygons.get(id);
                    if (cached && cached.length > 0)
                        segmentBuffers.set(idx, cached);
                });
            }
            else if (bufferConfig && bufferConfig.distance > 0) {
                const bufferPromises = segments.map(async (segment, index) => {
                    try {
                        const result = await elevationService.calculateStreamBuffer(projectId, segment.map(c => ({ lat: c.lat, lng: c.lng })), bufferDistanceMeters);
                        if (result.success) {
                            return { index, buffer: result.bufferPolygons };
                        }
                    }
                    catch (e) {
                        console.warn(`Failed to calculate buffer for segment ${index}:`, e);
                    }
                    return { index, buffer: [] };
                });
                const results = await Promise.all(bufferPromises);
                for (const { index, buffer } of results) {
                    if (buffer.length > 0) {
                        segmentBuffers.set(index, buffer);
                    }
                }
            }
            // Revert the create-mode preview BEFORE highlightStream runs so
            // it captures the true originals (not the preview styles) for
            // restore-on-unhighlight.
            clearCreatePreview();
            // Highlight each stream with its per-segment buffer. Collect the
            // committed records into a snapshot so the caller can promote
            // without waiting for React to re-render.
            const createSnapshot = new Map();
            for (let i = 0; i < selectedStreams.length; i++) {
                const stream = selectedStreams[i];
                const bufferPolygons = segmentBuffers.get(i) || [];
                const built = await highlightStream(stream.entity, stream.artifactId, stream.streamThreshold, stream.lineCoordinates, colorHex, 
                // Pass the CONVERTED meters (computed above), never the raw entry:
                // handing the raw feet value here is exactly what stored a 50 ft
                // buffer as 50 m.
                bufferConfig
                    ? { distanceMeters: bufferDistanceMeters, unit: bufferConfig.unit }
                    : undefined, bufferPolygons.length > 0 ? bufferPolygons : undefined, name, lineWidth);
                if (built)
                    createSnapshot.set(built.entityId, built);
            }
            // Compute the resulting groupId from the snapshot.
            const committedIds = Array.from(createSnapshot.keys()).sort();
            const newGroupId = committedIds.length > 0 ? `stream-group-${committedIds[0]}` : null;
            // Clear selection state and selectable highlights
            clearSelectionIndicators();
            clearSelectableHighlights();
            setSelectedStreams([]);
            setPendingClick(null);
            clearBufferPreview();
            return newGroupId ? { groupId: newGroupId, snapshot: createSnapshot } : null;
        }
        catch (error) {
            console.error('Failed to confirm highlight:', error);
            // Clear selection state and selectable highlights on error too
            clearSelectionIndicators();
            clearSelectableHighlights();
            setSelectedStreams([]);
            setPendingClick(null);
            clearBufferPreview();
            return null;
        }
        finally {
            setIsCalculatingBuffer(false);
        }
    }, [selectedStreams, highlightStream, clearSelectionIndicators, clearSelectableHighlights, projectId, findContiguousHighlighted, updateGroupColor, updateGroupLineWidth, highlightedStreams, clearBufferPreview]);
    // Cancel highlight dialog. Two modes:
    //  - EDIT: restore the highlight's pre-edit colour, width, and name
    //    from the snapshot taken at click time. previewColor and
    //    previewLineWidth already changed the live highlight; this puts
    //    it back.
    //  - CREATE: drop the in-progress selection entirely.
    const cancelHighlight = useCallback(() => {
        if (editBaselineRef.current) {
            const { entityId, colorHex, userLineWidth, name } = editBaselineRef.current;
            const group = findContiguousHighlighted(entityId);
            updateGroupColor(group, colorHex);
            updateGroupLineWidth(group, userLineWidth !== null && userLineWidth !== void 0 ? userLineWidth : DEFAULT_HIGHLIGHT_LINE_WIDTH);
            setHighlightedStreams(prev => {
                const newMap = new Map(prev);
                group.forEach(id => {
                    const h = newMap.get(id);
                    if (h)
                        newMap.set(id, { ...h, name });
                });
                return newMap;
            });
            editBaselineRef.current = null;
            setPendingClick(null);
            clearBufferPreview();
            return;
        }
        clearSelectionIndicators();
        clearSelectableHighlights();
        setSelectedStreams([]);
        setPendingClick(null);
        clearBufferPreview();
        clearCreatePreview();
    }, [clearSelectionIndicators, clearSelectableHighlights, findContiguousHighlighted, updateGroupColor, updateGroupLineWidth, clearBufferPreview, clearCreatePreview]);
    // Dismiss dialog without clearing selection — used by buffer step flow
    const dismissDialog = useCallback(() => {
        setPendingClick(null);
    }, []);
    // Discard the current highlight outright. In create mode this matches
    // cancelHighlight (drop the in-progress selection); in edit mode it
    // unhighlights every member of the contiguous group — destructive,
    // intended to back the dialog's "Discard" action with a confirm.
    const discardHighlight = useCallback(() => {
        if (editBaselineRef.current) {
            const { entityId } = editBaselineRef.current;
            const group = findContiguousHighlighted(entityId);
            group.forEach(id => unhighlightStream(id));
            editBaselineRef.current = null;
            setPendingClick(null);
            clearBufferPreview();
            return;
        }
        if (viewModeAnchorRef.current) {
            const { entityId } = viewModeAnchorRef.current;
            const group = findContiguousHighlighted(entityId);
            group.forEach(id => unhighlightStream(id));
            viewModeAnchorRef.current = null;
            setIsViewMode(false);
            setPendingClick(null);
            clearBufferPreview();
            return;
        }
        clearSelectionIndicators();
        clearSelectableHighlights();
        setSelectedStreams([]);
        setPendingClick(null);
        clearBufferPreview();
        clearCreatePreview();
    }, [clearSelectionIndicators, clearSelectableHighlights, findContiguousHighlighted, unhighlightStream, clearBufferPreview, clearCreatePreview]);
    // Update merged buffer whenever highlighted streams change
    useEffect(() => {
        updateMergedBuffer(highlightedStreams);
    }, [highlightedStreams, updateMergedBuffer]);
    // Handle artifact visibility changes (for overlay logic)
    // This syncs clone entity visibility with artifact visibility, but respects group visibility
    useEffect(() => {
        if (!highlightsDataSourceRef.current)
            return;
        highlightedStreams.forEach((highlight, entityId) => {
            var _a;
            const isSourceVisible = isArtifactVisible(highlight.artifactId);
            const highlightEntity = (_a = highlightsDataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.getById(entityId);
            // Find which group this stream belongs to
            const group = findContiguousHighlighted(entityId);
            const sortedIds = Array.from(group).sort();
            const groupId = `stream-group-${sortedIds[0]}`;
            const isGroupHidden = groupVisibility.get(groupId) === false;
            if (highlightEntity) {
                // Clone entity shows when: artifact is hidden AND group is not manually hidden
                highlightEntity.show = !isSourceVisible && !isGroupHidden;
            }
            // Also update the original entity visibility based on group setting
            if (highlight.entity) {
                highlight.entity.show = !isGroupHidden;
            }
        });
    }, [highlightedStreams, isArtifactVisible, loadedArtifacts, groupVisibility, findContiguousHighlighted]);
    // Sync merged buffer entities visibility and opacity with group state
    useEffect(() => {
        var _a;
        if (mergedBufferEntitiesRef.current.length === 0)
            return;
        if (highlightedStreams.size === 0)
            return;
        // Check if any group is hidden
        let anyGroupHidden = false;
        let effectiveOpacity = 1.0;
        // Get group state from first stream (all highlighted streams share state for now)
        const firstEntry = highlightedStreams.entries().next().value;
        if (firstEntry) {
            const [entityId] = firstEntry;
            const group = findContiguousHighlighted(entityId);
            const sortedIds = Array.from(group).sort();
            const groupId = `stream-group-${sortedIds[0]}`;
            anyGroupHidden = groupVisibility.get(groupId) === false;
            effectiveOpacity = (_a = groupOpacities.get(groupId)) !== null && _a !== void 0 ? _a : 1.0;
        }
        // Apply to all merged buffer entities
        mergedBufferEntitiesRef.current.forEach(entity => {
            entity.show = !anyGroupHidden;
            if (entity.polygon) {
                const highlight = highlightedStreams.values().next().value;
                if (highlight) {
                    entity.polygon.material = new Cesium.ColorMaterialProperty(highlight.highlightColor.withAlpha(effectiveOpacity * 0.3));
                }
            }
        });
    }, [groupVisibility, groupOpacities, highlightedStreams, findContiguousHighlighted]);
    // Restore from config (for scope). isRetry parameter mirrors the
    // contour hook's signature so captureMapState can call both with the
    // same shape; passed through as a no-op clear gate (the pendingRestores
    // queue handles late-loading artifacts automatically).
    const restoreFromConfig = useCallback(async (configs, isRetry = false) => {
        if (!isRetry) {
            unhighlightAll();
        }
        setPendingRestores(configs);
    }, [unhighlightAll]);
    // Process pending restores
    useEffect(() => {
        if (pendingRestores.length === 0 || !viewer || viewer.isDestroyed())
            return;
        const processRestores = async () => {
            var _a;
            const stillPending = [];
            for (const config of pendingRestores) {
                const artifact = loadedArtifacts.get(config.artifactId);
                if (!artifact || artifact.type !== 'vector') {
                    stillPending.push(config);
                    continue;
                }
                // Find entity by hash
                const dataSource = artifact.cesiumObject;
                if (!((_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values)) {
                    stillPending.push(config);
                    continue;
                }
                let targetEntity = null;
                const [, targetHash] = config.entityId.includes('|')
                    ? config.entityId.split('|')
                    : [null, null];
                for (const entity of dataSource.entities.values) {
                    if (entity.polyline) {
                        const hash = computeCoordHash(entity);
                        if (targetHash && hash === targetHash) {
                            targetEntity = entity;
                            break;
                        }
                    }
                }
                if (!targetEntity)
                    continue;
                const lineCoordinates = extractLineCoordinates(targetEntity);
                const bufferConfig = config.bufferDistanceMeters > 0
                    ? { distanceMeters: config.bufferDistanceMeters, unit: config.bufferUnit }
                    : undefined;
                // Pass precomputed buffer polygons if available
                const precomputedBufferPolygons = config.bufferPolygons && config.bufferPolygons.length > 0
                    ? config.bufferPolygons
                    : undefined;
                await highlightStream(targetEntity, config.artifactId, config.streamThreshold, lineCoordinates, config.color, bufferConfig, precomputedBufferPolygons, config.name, undefined);
            }
            if (stillPending.length !== pendingRestores.length) {
                setPendingRestores(stillPending);
            }
        };
        processRestores();
    }, [loadedArtifacts, pendingRestores, viewer, highlightStream]);
    // Get current state for saving to scope
    const getHighlightConfigs = useCallback(() => {
        return Array.from(highlightedStreams.values()).map(h => ({
            entityId: `${h.streamThreshold}|${computeCoordHash(h.entity)}`,
            color: h.highlightColor.toCssColorString(),
            artifactId: h.artifactId,
            streamThreshold: h.streamThreshold,
            bufferDistanceMeters: h.bufferDistanceMeters,
            bufferUnit: h.bufferUnit,
            bufferPolygons: h.bufferPolygons,
            name: h.name
        }));
    }, [highlightedStreams]);
    // Subscribe to scope events for clear and restore operations
    useEventSubscribe('scope:clearAll', () => {
        unhighlightAll();
    }, [unhighlightAll]);
    useEventSubscribe('scope:restore', (payload) => {
        if (payload.streamHighlights && payload.streamHighlights.length > 0) {
            restoreFromConfig(payload.streamHighlights);
        }
    }, [restoreFromConfig]);
    const toggleGroupVisibility = useCallback((groupId) => {
        var _a;
        if (!groupId.startsWith('stream-group-'))
            return;
        const firstEntityId = groupId.replace('stream-group-', '');
        const entityIds = findContiguousHighlighted(firstEntityId);
        const firstHighlight = highlightedStreams.get(firstEntityId);
        const currentlyVisible = groupVisibility.has(groupId) ? groupVisibility.get(groupId) : ((_a = firstHighlight === null || firstHighlight === void 0 ? void 0 : firstHighlight.entity.show) !== null && _a !== void 0 ? _a : false);
        const newVisible = !currentlyVisible;
        entityIds.forEach(entityId => {
            const highlight = highlightedStreams.get(entityId);
            if (highlight) {
                highlight.entity.show = newVisible;
                if (highlight.bufferEntity)
                    highlight.bufferEntity.show = newVisible;
            }
            if (highlightsDataSourceRef.current) {
                const cloneEntity = highlightsDataSourceRef.current.entities.getById(entityId);
                if (cloneEntity)
                    cloneEntity.show = !newVisible;
            }
        });
        // Also toggle merged buffer entities visibility
        mergedBufferEntitiesRef.current.forEach(entity => {
            entity.show = newVisible;
        });
        setGroupVisibility(prev => { const m = new Map(prev); m.set(groupId, newVisible); return m; });
    }, [highlightedStreams, findContiguousHighlighted, groupVisibility]);
    const isGroupVisible = useCallback((groupId) => {
        if (groupVisibility.has(groupId))
            return groupVisibility.get(groupId);
        const firstEntityId = groupId.replace('stream-group-', '');
        const highlight = highlightedStreams.get(firstEntityId);
        if (highlight)
            return highlight.entity.show;
        return false;
    }, [groupVisibility, highlightedStreams]);
    const updateGroupOpacity = useCallback((groupId, opacity) => {
        if (!groupId.startsWith('stream-group-'))
            return;
        const firstEntityId = groupId.replace('stream-group-', '');
        const entityIds = findContiguousHighlighted(firstEntityId);
        entityIds.forEach(entityId => {
            const highlight = highlightedStreams.get(entityId);
            if (!highlight)
                return;
            if (highlight.entity.polyline) {
                highlight.entity.polyline.material = new Cesium.ColorMaterialProperty(highlight.highlightColor.withAlpha(opacity));
            }
            if (highlightsDataSourceRef.current) {
                const cloneEntity = highlightsDataSourceRef.current.entities.getById(entityId);
                if (cloneEntity === null || cloneEntity === void 0 ? void 0 : cloneEntity.polyline) {
                    cloneEntity.polyline.material = new Cesium.ColorMaterialProperty(highlight.highlightColor.withAlpha(opacity));
                }
            }
        });
        // Update merged buffer entities opacity
        mergedBufferEntitiesRef.current.forEach(entity => {
            if (entity.polygon) {
                const highlight = highlightedStreams.values().next().value;
                if (highlight) {
                    entity.polygon.material = new Cesium.ColorMaterialProperty(highlight.highlightColor.withAlpha(opacity * 0.3));
                }
            }
        });
        setGroupOpacities(prev => { const m = new Map(prev); m.set(groupId, opacity); return m; });
    }, [highlightedStreams, findContiguousHighlighted]);
    const getGroupOpacity = useCallback((groupId) => {
        var _a;
        return (_a = groupOpacities.get(groupId)) !== null && _a !== void 0 ? _a : 1.0;
    }, [groupOpacities]);
    const updateStreamName = useCallback((entityId, name) => {
        setHighlightedStreams(prev => {
            const highlight = prev.get(entityId);
            if (!highlight)
                return prev;
            const newMap = new Map(prev);
            newMap.set(entityId, { ...highlight, name });
            return newMap;
        });
    }, []);
    const updateGroupName = useCallback((groupId, name) => {
        const contiguousIds = findContiguousHighlighted(groupId);
        setHighlightedStreams(prev => {
            const newMap = new Map(prev);
            contiguousIds.forEach(id => {
                const highlight = newMap.get(id);
                if (highlight) {
                    newMap.set(id, { ...highlight, name });
                }
            });
            return newMap;
        });
    }, [findContiguousHighlighted]);
    // Pre-fill values for the dialog when re-opening over an already-
    // highlighted stream. Null when creating a new highlight.
    const editingDefaults = (() => {
        var _a;
        const baseline = editBaselineRef.current;
        if (!pendingClick || !baseline)
            return null;
        const existing = highlightedStreams.get(baseline.entityId);
        if (!existing)
            return null;
        const group = findContiguousHighlighted(baseline.entityId);
        const groupPolygons = [];
        group.forEach(id => {
            const h = highlightedStreams.get(id);
            if (h === null || h === void 0 ? void 0 : h.bufferPolygons)
                groupPolygons.push(...h.bufferPolygons);
        });
        return {
            entityId: baseline.entityId,
            color: existing.colorHex,
            lineWidth: (_a = existing.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH,
            name: existing.name,
            bufferDistanceMeters: existing.bufferDistanceMeters,
            bufferUnit: existing.bufferUnit,
            lineCoordinates: existing.lineCoordinates,
            bufferAreaSqMeters: groupPolygons.length > 0 ? calculateBufferPolygonsAreaM2(groupPolygons) : 0,
        };
    })();
    // Read-only snapshot for the view-mode dialog. Mirrors editingDefaults
    // but also sums the line length across every segment in the contiguous
    // group so the dialog can show a single "Length: X" headline.
    const viewingDefaults = (() => {
        var _a;
        const anchor = viewModeAnchorRef.current;
        if (!pendingClick || !anchor)
            return null;
        const existing = highlightedStreams.get(anchor.entityId);
        if (!existing)
            return null;
        const group = findContiguousHighlighted(anchor.entityId);
        let totalDistanceMeters = 0;
        const groupPolygons = [];
        group.forEach(id => {
            const h = highlightedStreams.get(id);
            if (!h)
                return;
            for (let i = 0; i < h.lineCoordinates.length - 1; i++) {
                totalDistanceMeters += haversineDistance(h.lineCoordinates[i], h.lineCoordinates[i + 1]);
            }
            if (h.bufferPolygons)
                groupPolygons.push(...h.bufferPolygons);
        });
        return {
            entityId: anchor.entityId,
            color: existing.colorHex,
            lineWidth: (_a = existing.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH,
            name: existing.name,
            bufferDistanceMeters: existing.bufferDistanceMeters,
            bufferUnit: existing.bufferUnit,
            totalDistanceMeters,
            segmentCount: group.size,
            bufferAreaSqMeters: groupPolygons.length > 0 ? calculateBufferPolygonsAreaM2(groupPolygons) : 0,
        };
    })();
    return {
        highlightedStreams,
        pendingClick,
        selectedStreams,
        isCalculatingBuffer,
        hoveredStream,
        editingDefaults,
        viewingDefaults,
        isViewMode,
        promoteToEdit,
        handleMapClick,
        handleMapDoubleClick,
        handleMapHover,
        confirmHighlight,
        dismissDialog,
        cancelHighlight,
        discardHighlight,
        previewColor,
        previewLineWidth,
        previewBuffer,
        clearBufferPreview,
        startEditFromArtifact,
        unhighlightStream,
        unhighlightAll,
        restoreFromConfig,
        getHighlightConfigs,
        calculateBuffer,
        highlightCount: highlightedStreams.size,
        selectionCount: selectedStreams.length,
        getAllContiguousGroups,
        findContiguousHighlighted,
        toggleGroupVisibility,
        isGroupVisible,
        updateGroupOpacity,
        getGroupOpacity,
        updateStreamName,
        updateGroupName,
    };
};
